import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/biometric-policy")({
  component: BioPolicyPage,
});

function BioPolicyPage() {
  return (
    <div className="bg-white px-6 py-32 lg:px-8">
      <div className="mx-auto max-w-3xl text-base/7 text-gray-700">
        <p className="text-base/7 font-semibold text-green-600">Flash Biometrics</p>
        <h1 className="mt-2 text-4xl font-semibold tracking-tight text-pretty text-gray-900 sm:text-5xl">
          Biometric Data Privacy Policy
        </h1>
        <div className="mt-10 max-w-2xl">
          <p className="mt-6">
            <strong className="font-semibold text-gray-900">Last Updated:</strong> April 27, 2026
          </p>
          <p className="mt-6">
            App-Order LLC, ("Flash," "Flash Biometrics," "we," "us," or "ours") has established the
            following Biometric Data Privacy Policy (the "Policy") for end users and data subjects
            of Flash's biometric authentication system (the "System"). This Policy describes how
            Flash treats facial images and other data that might be considered "biometric
            identifiers" or "biometric information" under applicable law (collectively, "Biometric
            Data") that is collected, obtained, and/or processed by Flash from end users and data
            subjects in the United States through its operation of the System. Flash reserves the
            right to amend this Policy at any time, without notice.
          </p>

          <h2 className="mt-16 text-3xl font-semibold tracking-tight text-pretty text-gray-900">
            Collection and Processing
          </h2>

          <p className="mt-6">
            Biometric systems are consumer-based systems that scan an individual's face, finger,
            hand or other physical feature for purposes of recognition and/or verification. These
            systems generate unique data points and create a unique mathematical representation or
            algorithm that is used to identify or verify an individual's identity. This applies in
            instances like when an individual uses their face or fingerprint as an authentication
            measure. Flash's System can match your face to your picture, for example to make
            checking-in for an event faster and easier. This is the only thing we do with your
            biometric data.
          </p>

          <p className="mt-6">
            Any Biometric Data collected or processed by us will be used solely for purpose of
            identity verification and to comply with our legal obligations under applicable law.
            Further, where permitted by applicable law, we may use Biometric Data to improve our
            products, services, and related offerings. In the event that we begin collecting or
            using Biometric Data in connection with the System for any additional purposes, we will
            update this Policy.
          </p>

          <div className="my-8 h-px w-full bg-gray-400"></div>

          <h2 className="mt-16 text-3xl font-semibold tracking-tight text-pretty text-gray-900">
            Disclosures
          </h2>
          <p className="mt-6">
            We may disclose Biometric Data with our third-party service providers (e.g., cloud
            hosting providers) that assist with or otherwise facilitate our operation of the System
            to facilitate your use of the System.
          </p>

          <ul role="list" className="mt-8 max-w-xl space-y-8 text-gray-600">
            <li className="flex gap-x-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="4"
                height="4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                className="mt-1 size-5 flex-none text-green-600"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <circle cx="12" cy="12" r="1"></circle>
              </svg>
              <span>when required to do so by law</span>
            </li>

            <li className="flex gap-x-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="4"
                height="4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                className="mt-1 size-5 flex-none text-green-600"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <circle cx="12" cy="12" r="1"></circle>
              </svg>
              <span>when necessary to establish, exercise, or protect our rights</span>
            </li>

            <li className="flex gap-x-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="4"
                height="4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                className="mt-1 size-5 flex-none text-green-600"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <circle cx="12" cy="12" r="1"></circle>
              </svg>
              <span>to protect the vital interests of natural persons</span>
            </li>

            <li className="flex gap-x-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="4"
                height="4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                className="mt-1 size-5 flex-none text-green-600"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <circle cx="12" cy="12" r="1"></circle>
              </svg>
              <span>to investigate fraud or other criminal acts</span>
            </li>

            <li className="flex gap-x-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="4"
                height="4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                className="mt-1 size-5 flex-none text-green-600"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <circle cx="12" cy="12" r="1"></circle>
              </svg>
              <span>
                in the context of a merger, acquisition, or similar commercial transaction.
              </span>
            </li>
          </ul>
          <p className="mt-8">
            Any Biometric Data disclosed by us will be solely for these purposes.
          </p>

          <div className="my-8 h-px w-full bg-gray-400"></div>

          <h2 className="mt-16 text-3xl font-semibold tracking-tight text-pretty text-gray-900">
            Protocol for Responding to Biometric Data Security Incident
          </h2>

          <p className="mt-6">
            When Flash becomes aware of a security breach that may have occurred, Flash will conduct
            a prompt investigation to determine the likelihood that the Biometric Data has been or
            will be misused.
          </p>

          <p className="mt-6">
            Flash will give notice, without unreasonable delay, to the affected residents as
            required by applicable law, unless the investigation determines that misuse has not
            occurred and is not reasonably likely to occur. If required, notice will be provided not
            later than 30 days after the date of the determination that a security breach occurred,
            consistent with the legitimate needs of law enforcement and measures needed to determine
            the scope of the breach and restore reasonable integrity to the system.
          </p>

          <div className="my-8 h-px w-full bg-gray-400"></div>

          <h2 className="mt-16 text-3xl font-semibold tracking-tight text-pretty text-gray-900">
            Retention and Destruction
          </h2>
          <p className="mt-6">
            We will retain any biometric information collected only for as long as necessary to
            fulfill the initial purpose for which it was collected, or for up to two (2) years after
            the last interaction with our services, whichever comes first. After that, the data will
            be permanently deleted.
          </p>

          <div className="my-8 h-px w-full bg-gray-400"></div>

          <h2 className="mt-16 text-3xl font-semibold tracking-tight text-pretty text-gray-900">
            Contact Information
          </h2>
          <figure className="mt-8 border-l border-green-600 pl-9">
            <blockquote className="font-semibold text-gray-900">
              <p>App-Order.com</p>

              <p>1094 E Sahara Ave.</p>

              <p>Las Vegas, NV 89104</p>

              <p>800-884-7820 Office</p>

              <p>800-536-0963 Fax</p>

              <p>help@flashbiometrics.com</p>
            </blockquote>
          </figure>
        </div>
      </div>
    </div>
  );
}
